import type { DefaultError, QueryKey } from '@tanstack/query-core';
import { UseSuspenseInfiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchTasksParams, fetchTasks } from 'api/tasks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Task } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const DEFAULT_PAGE_LIMIT = 20;

type Options = Omit<
    UseSuspenseInfiniteQueryOptions<
        PaginatedData<Task>,
        DefaultError,
        Task,
        PaginatedData<Task>,
        QueryKey,
        number
    >,
    'queryKey' | 'initialPageParam' | 'select' | 'getNextPageParam'
>;

const useInfiniteTasks = (
    params: Omit<FetchTasksParams, 'page'> = {},
    options: Options = {} as Options,
) => {
    const { limit = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const nextParams = { limit, ...rest };
    const { accountId } = useCurrentAccount();
    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.tasksWithFilters(accountId, params),
        queryFn: ({ pageParam }) => fetchTasks(accountId, { page: pageParam, ...nextParams }),
        getNextPageParam: lastPage =>
            lastPage.meta.current_page < lastPage.meta.last_page
                ? lastPage.meta.current_page + 1
                : undefined,
        staleTime: 0,
        select: data => ({
            tasks: data.pages.flatMap(p => p.data),
            meta: data.pages.at(-1)?.meta,
        }),
        ...options,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteTasks };
